import { enableProdMode, importProvidersFrom, APP_INITIALIZER, LOCALE_ID, ErrorHandler } from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import { provideRouter } from '@angular/router';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideHttpClient, withInterceptors, withFetch, HTTP_INTERCEPTORS } from '@angular/common/http';
import { DateAdapter, MAT_DATE_LOCALE, MAT_DATE_FORMATS } from '@angular/material/core';
import { registerLocaleData } from '@angular/common';
import localeDE from '@angular/common/locales/de';
import localeDeExtra from '@angular/common/locales/extra/de';
import localeEN from '@angular/common/locales/en';
import localeAR from '@angular/common/locales/ar';
import localeFR from '@angular/common/locales/fr';
import localeTR from '@angular/common/locales/tr';
import localeRO from '@angular/common/locales/ro';
import localePL from '@angular/common/locales/pl';

import { AppComponent } from './app/app.component';
import { environment } from './environments/environment';
import { routes } from './app/app-routing.module';
import { authInterceptor } from './app/modules/auth/services/guard/auth.interceptor.fn';
import { clientInfoInterceptor } from './app/core/services/client-info/client-info.interceptor';
import { ClientInfoService } from './app/core/services/client-info/client-info.service';
import { CustomDateAdapter } from './app/core/_helpers/custom.date.adapter';
import { MatDatepickerIntl } from '@angular/material/datepicker';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { TranslatedDatepickerIntl, TranslatedPaginatorIntl } from './app/core/_helpers/translated-mat-intl';
import { APP_DATE_FORMATS } from './app/core/_helpers/app-date-formats';
import { GlobalErrorHandler } from './app/core/services/logging/global-error-handler.service';
import { LoggingInterceptor } from './app/core/services/logging/logging-interceptor.service';
import { ClientLoggerService } from './app/core/services/logging/client-logger.service';
import { BrowserInfoService } from './app/core/services/logging/browser-info.service';
import { PerformanceTrackerService } from './app/core/services/logging/performance-tracker.service';
import { LogDeduplicationService } from './app/core/services/logging/log-deduplication.service';
import { ConsoleErrorInterceptorService } from './app/core/services/logging/console-error-interceptor.service';
import { GlobalErrorListenersService } from './app/core/services/logging/global-error-listeners.service';
import { TranslateModule, TranslateService, TranslateLoader } from '@ngx-translate/core';
import { HttpClient } from '@angular/common/http';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { IosTextOverrideLoader } from './app/core/i18n/ios-text-overrides';
import { timeout, catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { Capacitor } from '@capacitor/core';
import { ServiceWorkerModule } from '@angular/service-worker';
import { IonicModule } from '@ionic/angular';
import { ToastrModule } from 'ngx-toastr';
import { TranslatedToastComponent } from './app/core/component/translated-toast/translated-toast.component';
import { MaterialModule } from './app/core/modules/material/material.module';
import { SharedCoreModule } from './app/shared/shared-core.module';
import { TourRegistry } from './app/onboarding/services/tour-registry.service';
import { ALL_TOUR_CONFIGS } from './app/onboarding/tutorials';
import { MentorOutcomeService } from './app/onboarding/services/mentor-outcome.service';
import {
  SEARCH_PROVIDER,
  SEARCH_ACTION_HANDLER,
  SEARCH_STRATEGIES,
  ExactMatchStrategy,
  FuzzyMatchStrategy,
  NGramMatchStrategy,
  KeywordMatchStrategy,
  SemanticMatchStrategy,
  IntentMatchStrategy,
  TokenMatchStrategy,
  CompoundSplitStrategy,
} from './app/core/services/search';
import { SettingsSearchProvider, SettingsSearchActionHandler } from './app/modules/settings/search';
import {
  PermissionsSearchProvider,
  PermissionsSearchActionHandler,
} from './app/modules/employee/component/user-permissions-dialog/search';
import {
  LanguageService,
  DEFAULT_LANGUAGE,
} from './app/core/services/language/language.service';

// Register locale data for all supported languages
registerLocaleData(localeDE, 'de', localeDeExtra);
registerLocaleData(localeEN, 'en');
registerLocaleData(localeAR, 'ar');
registerLocaleData(localeFR, 'fr');
registerLocaleData(localeTR, 'tr');
registerLocaleData(localeRO, 'ro');
registerLocaleData(localePL, 'pl');

if (environment.production) {
  enableProdMode();
}

/**
 * Checks if the deployed app version has changed since last visit.
 * If yes, deregisters stale Service Workers and clears their caches,
 * then reloads so the browser fetches fresh assets (including i18n).
 *
 * Uses `ngsw-bypass` header to bypass even a stuck Angular SW.
 * Runs BEFORE Angular bootstrap to fix the problem before the app loads stale translations.
 */
async function checkVersionAndClearStaleCache(): Promise<void> {
  // Only relevant for web production (SW is only enabled there)
  if (!environment.production || Capacitor.isNativePlatform()) return;

  try {
    const resp = await fetch('/assets/version.json', {
      headers: { 'ngsw-bypass': 'true' },
      cache: 'no-store',
    });
    if (!resp.ok) return;

    const { version: serverVersion } = await resp.json();
    if (!serverVersion) return;

    const cachedVersion = localStorage.getItem('app-version');

    if (cachedVersion && cachedVersion !== serverVersion) {
      console.log(`[SW Recovery] Version mismatch: cached=${cachedVersion}, server=${serverVersion}. Clearing stale SW...`);

      // Deregister all Angular service workers
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(
          registrations
            .filter(r => r.active?.scriptURL?.includes('ngsw'))
            .map(r => r.unregister())
        );
      }

      // Clear all ngsw caches
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(
          cacheNames
            .filter(name => name.startsWith('ngsw:'))
            .map(name => caches.delete(name))
        );
      }

      localStorage.setItem('app-version', serverVersion);
      window.location.reload();
      return; // Unreachable after reload, but makes intent clear
    }

    // Store current version for next comparison
    localStorage.setItem('app-version', serverVersion);
  } catch {
    // Fetch failed (offline, network error) - ignore, let the app boot normally
  }
}

// AoT requires an exported function for factories
export function HttpLoaderFactory(http: HttpClient) {
  // iOS: kauf-lenkende Texte werden zentral neutralisiert (App-Store-Guideline 3.1.1).
  return new IosTextOverrideLoader(new TranslateHttpLoader(http), Capacitor.getPlatform() === 'ios');
}

// Get current locale for Angular's locale system.
// Nutzt zentrale Normalisierung aus LanguageService (siehe UX-Audit
// findings/11 #1, 2026-05-25): alte Storage-Werte wie 'de-DE' werden
// on-the-fly migriert, sonst Browser-Fallback.
export function getLocaleId(): string {
  const stored = LanguageService.getStoredLanguage();
  if (stored) {
    return stored;
  }
  if (typeof navigator !== 'undefined') {
    return LanguageService.normalize(navigator.language);
  }
  return DEFAULT_LANGUAGE;
}

export function appInitializerFactory(
  translate: TranslateService
): () => Promise<any> {
  return () =>
    new Promise<any>((resolve: any) => {
      const isBrowser =
        typeof window !== 'undefined' &&
        typeof document !== 'undefined' &&
        typeof navigator !== 'undefined';

      let langToSet = DEFAULT_LANGUAGE;
      if (isBrowser) {
        const stored = LanguageService.getStoredLanguage();
        if (stored) {
          langToSet = stored;
        } else {
          langToSet = LanguageService.normalize(navigator.language);
        }
        // <html lang> synchron setzen, damit Screen-Reader und CSS-Selektoren
        // den korrekten Wert sehen — analog zum Inline-Script in index.html,
        // aber hier doppelt abgesichert nach evtl. Migration.
        if (document.documentElement) {
          document.documentElement.lang = langToSet;
        }
      }

      // PERFORMANCE: Set default lang to the SAME as the active lang to avoid
      // loading two translation files during bootstrap. setDefaultLang() triggers
      // an HTTP request for the default language file. Previously this was always 'en',
      // causing both en.json (~500KB) and de.json (~530KB) to load on every page visit.
      // After bootstrap, we lazily load 'en' as fallback for missing keys (non-blocking).
      translate.setDefaultLang(langToSet);

      // Load translation files with short timeout to prevent app blocking
      translate.use(langToSet).pipe(
        timeout(2000),
        catchError((err) => {
          console.warn(`[AppInit] Translation load timeout/failed for '${langToSet}', trying fallback 'en'`);
          // Only load 'en' as fallback if the primary language failed AND is not already 'en'
          if (langToSet !== 'en') {
            translate.setDefaultLang('en');
            return translate.use('en').pipe(
              timeout(1000),
              catchError(() => {
                console.warn('[AppInit] Fallback translation also failed, continuing without translations');
                return of(null);
              })
            );
          }
          console.warn('[AppInit] English translation failed, continuing without translations');
          return of(null);
        })
      ).subscribe({
        next: () => {
          resolve(null);
          // PERFORMANCE: Lazily load English as fallback language for missing keys
          // This runs AFTER bootstrap (non-blocking) so it doesn't delay page render.
          // Without this, missing keys in de.json/ar.json would show raw key strings.
          if (langToSet !== 'en') {
            setTimeout(() => {
              translate.setDefaultLang('en');
            }, 3000);
          }
        },
        error: () => resolve(null)
      });
    });
}

// Console Error Interceptor Initializer
export function consoleErrorInterceptorInitializer(
  consoleErrorInterceptor: ConsoleErrorInterceptorService
): () => void {
  return () => {
    consoleErrorInterceptor.initialize();
  };
}

// Global Error Listeners Initializer - fängt unhandledrejection & window.onerror
// (Fehler außerhalb der Angular-Zone, u. a. native Capacitor-Plugin-/Promise-Fehler).
// Hinweis: Listener-Registrierung erfolgt in GlobalErrorListenersService.initialize().
export function globalErrorListenersInitializer(
  globalErrorListeners: GlobalErrorListenersService
): () => void {
  return () => {
    globalErrorListeners.initialize();
  };
}

// Client Info Service Initializer - sammelt Client-Infos für Seq-Logging
export function clientInfoInitializer(
  clientInfoService: ClientInfoService
): () => Promise<void> {
  return async () => {
    try {
      await clientInfoService.initialize();
      console.log('[ClientInfo] Initialized successfully');
    } catch (error) {
      console.warn('[ClientInfo] Failed to initialize:', error);
      // Nicht kritisch - App kann ohne Client-Info weiterlaufen
    }
  };
}

/**
 * Onboarding-Plattform V1: registriert alle Tour-Configs aus
 * `tutorials/index.ts` in der `TourRegistry` und validiert sie
 * (Anchor-IDs, Kapitel-Struktur). Fail-Fast: ein Typo bricht den
 * App-Start im Dev-Build.
 *
 * Siehe docs/onboarding-refactor/phases/phase-03-tour-coordinator.md
 */
export function tourRegistryInitializer(
  registry: TourRegistry
): () => void {
  return () => {
    for (const config of ALL_TOUR_CONFIGS) {
      try {
        registry.register(config);
      } catch (err) {
        // Dev-Build: Fail-Fast. Prod: loggen, damit die App trotzdem startet.
        if (!environment.production) {
          throw err;
        }
        console.error('[TourRegistry] invalide Tour-Config:', err);
      }
    }
  };
}

// Material-Datepicker-Formate: siehe `core/_helpers/app-date-formats.ts` (auch im MaterialModule).

/**
 * iOS-App: kein automatisches Hineinzoomen beim Antippen eines Eingabefelds.
 *
 * WKWebView zoomt in jedes Feld mit weniger als 16 px Schrift (`--fs-base` = 14 px) — und
 * weil `ios.scrollEnabled: false` gilt, kommt niemand mehr heraus. Nach dem Login stand die
 * App deshalb vergrößert da: Menüleiste weg, Burger-Menü außerhalb, Text links und rechts
 * abgeschnitten (Befund am iPhone, 24.09.2026). Nur nativ auf iOS: Im Browser würde
 * `maximum-scale` auf Android das Zwei-Finger-Zoomen sperren.
 *
 * Das ist nur das **Sicherheitsnetz**. Die eigentliche Regel steht im XDS-System
 * (`_xds-components.css`, iOS-Zoom-Schutz): Jedes Eingabefeld hat dort auf iOS mindestens
 * 16 px. Geprüft von `ios-input-zoom.vitest.spec.ts`.
 */
function verhindereIosEingabeZoom(): void {
  if (Capacitor.getPlatform() !== 'ios') return;
  const viewport = document.querySelector('meta[name="viewport"]');
  const inhalt = viewport?.getAttribute('content') ?? '';
  if (viewport && !/maximum-scale/.test(inhalt)) {
    viewport.setAttribute('content', `${inhalt}, maximum-scale=1`);
  }
}
verhindereIosEingabeZoom();

// Run version check before bootstrapping Angular.
// If a stale SW is detected, the page reloads and bootstrap never runs on first pass.
checkVersionAndClearStaleCache().then(() => bootstrapApplication(AppComponent, {
  providers: [
    provideRouter(routes),
    provideAnimations(),
    provideHttpClient(
      withInterceptors([clientInfoInterceptor, authInterceptor]),
      withFetch() // Use native Fetch API for better performance
    ),
    { provide: LOCALE_ID, useFactory: getLocaleId },
    // Startwert aus der gespeicherten Sprache; danach folgt jeder CustomDateAdapter selbst dem
    // LanguageService (je Injector eine Instanz, siehe custom.date.adapter.ts).
    { provide: MAT_DATE_LOCALE, useFactory: getLocaleId },
    { provide: MAT_DATE_FORMATS, useValue: APP_DATE_FORMATS },
    { provide: DateAdapter, useClass: CustomDateAdapter },
    // Kalender- und Seitenbeschriftungen nach App-Sprache (translated-mat-intl.ts).
    { provide: MatDatepickerIntl, useClass: TranslatedDatepickerIntl },
    { provide: MatPaginatorIntl, useClass: TranslatedPaginatorIntl },
    {
      provide: APP_INITIALIZER,
      useFactory: appInitializerFactory,
      deps: [TranslateService],
      multi: true,
    },
    // Client Logging System (Frontend Error & Performance Tracking)
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    { provide: HTTP_INTERCEPTORS, useClass: LoggingInterceptor, multi: true },
    ClientLoggerService,
    BrowserInfoService,
    PerformanceTrackerService,
    LogDeduplicationService,
    ConsoleErrorInterceptorService,
    {
      provide: APP_INITIALIZER,
      useFactory: consoleErrorInterceptorInitializer,
      deps: [ConsoleErrorInterceptorService],
      multi: true,
    },
    // Globale Error-Listener (unhandledrejection + window.onerror) — schließt die
    // Lücke des Angular-ErrorHandlers für Fehler außerhalb der Zone (native
    // Capacitor-Plugin-/Promise-Fehler), damit auch diese im Seq-Log landen.
    GlobalErrorListenersService,
    {
      provide: APP_INITIALIZER,
      useFactory: globalErrorListenersInitializer,
      deps: [GlobalErrorListenersService],
      multi: true,
    },
    // Client Info Service - sammelt Client-Infos (Plattform, Version, OS) für Seq-Logging
    ClientInfoService,
    {
      provide: APP_INITIALIZER,
      useFactory: clientInfoInitializer,
      deps: [ClientInfoService],
      multi: true,
    },
    // Onboarding-Plattform V1 — TourRegistry-Bootstrap.
    // Phase 3; ALL_TOUR_CONFIGS ist temporaer leer, wird in Phase 6 gefuellt.
    {
      provide: APP_INITIALIZER,
      useFactory: tourRegistryInitializer,
      deps: [TourRegistry],
      multi: true,
    },
    // Mentor AP-9: MentorOutcomeService eager instanziieren, damit seine
    // Domain-Event-Subscription schon vor dem ersten Tour-Start steht und
    // keine fruehen fachlichen Outcomes (erstes Muster/Schicht/Freigabe)
    // verpasst werden.
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => Promise.resolve(),
      deps: [MentorOutcomeService],
      multi: true,
    },
    // Generic Search Infrastructure (core/services/search).
    // Strategies, providers and action handlers are wired here via multi-tokens.
    // Domain data (synonyms, typos, keywords) is lazy-loaded on first search.
    { provide: SEARCH_STRATEGIES, useExisting: ExactMatchStrategy, multi: true },
    { provide: SEARCH_STRATEGIES, useExisting: FuzzyMatchStrategy, multi: true },
    { provide: SEARCH_STRATEGIES, useExisting: NGramMatchStrategy, multi: true },
    { provide: SEARCH_STRATEGIES, useExisting: KeywordMatchStrategy, multi: true },
    { provide: SEARCH_STRATEGIES, useExisting: SemanticMatchStrategy, multi: true },
    { provide: SEARCH_STRATEGIES, useExisting: IntentMatchStrategy, multi: true },
    { provide: SEARCH_STRATEGIES, useExisting: TokenMatchStrategy, multi: true },
    { provide: SEARCH_STRATEGIES, useExisting: CompoundSplitStrategy, multi: true },
    { provide: SEARCH_PROVIDER, useExisting: SettingsSearchProvider, multi: true },
    { provide: SEARCH_PROVIDER, useExisting: PermissionsSearchProvider, multi: true },
    { provide: SEARCH_ACTION_HANDLER, useExisting: SettingsSearchActionHandler, multi: true },
    { provide: SEARCH_ACTION_HANDLER, useExisting: PermissionsSearchActionHandler, multi: true },
    {
      provide: APP_INITIALIZER,
      multi: true,
      useFactory: () => () => Promise.resolve(),
      deps: [SettingsSearchProvider, PermissionsSearchProvider],
    },
    importProvidersFrom(
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient],
        },
      }),
      ServiceWorkerModule.register('ngsw-worker.js', {
        // Only enable Service Worker in production AND when NOT running in native Capacitor app
        // Service Workers can cause issues in native apps (caching conflicts, white screens)
        enabled: environment.production && !Capacitor.isNativePlatform(),
      }),
      IonicModule.forRoot(),
      ToastrModule.forRoot({
        // Schließen-Knopf mit übersetztem Label (die Bibliothek hat „Close" fest im Template).
        toastComponent: TranslatedToastComponent,
        preventDuplicates: true,
        countDuplicates: true,
        resetTimeoutOnDuplicate: true,
        includeTitleDuplicates: true,
      }),
      MaterialModule,
      SharedCoreModule
    ),
  ],
}).catch((err) => console.error(err)));

// Native platform: Unregister any existing Service Workers to prevent caching issues
// ServiceWorkerModule.register() handles registration for web (enabled flag already checks isNativePlatform)
if ('serviceWorker' in navigator && Capacitor.isNativePlatform()) {
  navigator.serviceWorker.getRegistrations().then((registrations) => {
    registrations.forEach((registration) => {
      registration.unregister().then((success) => {
        if (success) {
          console.log('[Native] Service Worker unregistered successfully');
        }
      });
    });
  });
  // Also clear caches that might have been created by Service Worker
  if ('caches' in window) {
    caches.keys().then((cacheNames) => {
      cacheNames.forEach((cacheName) => {
        caches.delete(cacheName).then(() => {
          console.log('[Native] Cache deleted:', cacheName);
        });
      });
    });
  }
}
